import React, { useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  X, FileText, ClipboardPaste, CheckCircle2, AlertTriangle,
  Plus, Trash2, ChevronDown, ChevronUp, Upload,
} from "lucide-react";
import host from "../../../../global.jsx";
import QuestionEditor, { newQuestion, newOption, InlineError } from "./QuestionFormModal";

const SAMPLE = `Q: What is the time complexity of binary search?
A) O(n)
B) O(log n) *
C) O(n log n)

Q: Which keyword declares a constant in JavaScript?
A) let
B) var
C) const
Answer: C`;

/**
 * Parses the plaintext format people already have their questions in.
 * Tolerates "A)" / "1." / "-" markers, a trailing "*" on the correct option, or a
 * separate "Answer: B" line. Rows that fail come back with `error` set rather than
 * being dropped, so the user can see what didn't work.
 */
export const parseMCQText = (raw) => {
  const blocks = String(raw || "")
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);

  return blocks.map((block) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    let text = "";
    const options = [];
    let markedIndex = -1;
    let answerLine = "";

    lines.forEach((line) => {
      const answerMatch = line.match(/^answer\s*[:\-]\s*(.+)$/i);
      if (answerMatch) {
        answerLine = answerMatch[1].trim();
        return;
      }
      const optionMatch = line.match(/^(?:\(?([A-Fa-f])[).]|([1-6])[).]|[-•])\s*(.+)$/);
      if (optionMatch && (text || options.length)) {
        let body = optionMatch[3].trim();
        if (/\s\*$/.test(body)) {
          markedIndex = options.length;
          body = body.replace(/\s*\*\s*$/, "").trim();
        }
        options.push(body);
        return;
      }
      const questionMatch = line.match(/^(?:Q\s*[:.)-]?\s*|\d+[).]\s*)(.+)$/i);
      if (!text) {
        text = (questionMatch ? questionMatch[1] : line).trim();
      } else if (!options.length) {
        text += ` ${line}`;
      }
    });

    // An explicit "Answer:" line wins over a "*" mark.
    let correctIndex = markedIndex;
    if (answerLine) {
      const letter = answerLine.toUpperCase();
      if (/^[A-F]$/.test(letter)) {
        correctIndex = letter.charCodeAt(0) - 65;
      } else {
        const byText = options.findIndex(
          (o) => o.toLowerCase() === answerLine.toLowerCase()
        );
        if (byText >= 0) correctIndex = byText;
      }
    }

    let error = "";
    if (!text) error = "No question text found";
    else if (options.length < 2) error = "Needs at least 2 options";
    else if (correctIndex < 0 || correctIndex >= options.length)
      error = "No correct answer marked";
    else if (new Set(options.map((o) => o.toLowerCase())).size !== options.length)
      error = "Duplicate options";

    return { text, options, correctIndex, error };
  });
};

const rowToQuestion = (row) => {
  const options = row.options.map((t) => newOption(t));
  return {
    id: `q_${crypto.randomUUID()}`,
    text: row.text,
    options,
    correct_option_id: options[row.correctIndex]?.id ?? null,
  };
};

/**
 * Paste text or upload a PDF, preview what parsed, then accept. Nothing reaches the
 * set until the user accepts — PDF output in particular used to be saved unreviewed.
 */
const ImportDialog = ({ onClose, onAccept }) => {
  const accessToken = Cookies.get("accessToken");
  const [tab, setTab] = useState("pdf");
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [fileName, setFileName] = useState("");

  const handleParseText = () => {
    const parsed = parseMCQText(raw);
    if (!parsed.length) {
      toast.error("Nothing to parse — paste some questions first");
      return;
    }
    setRows(parsed);
  };

  const handlePdf = async (file) => {
    if (!file) return;
    setIsParsing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${host}/parse_mcq_pdf`, fd, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const parsed = (res.data.questions || []).map((q) => ({
        text: q.text,
        options: (q.options || []).map((o) => o.text),
        correctIndex: (q.options || []).findIndex((o) => o.id === q.correct_option_id),
        error: q.correct_option_id ? "" : "No correct answer matched",
      }));
      if (!parsed.length) toast.error("No questions could be read from that PDF");
      setRows(parsed);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error processing PDF");
    } finally {
      setIsParsing(false);
    }
  };

  const valid = (rows || []).filter((r) => !r.error);
  const broken = (rows || []).filter((r) => r.error);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-bgPrimary dark:glass-effect rounded-2xl border border-white/10 shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold dark:text-white text-textPrimary">
            Import Questions
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 dark:text-gray-400 text-textSecondary transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {!rows && (
          <>
            <div className="inline-flex items-center gap-1 p-1 rounded-full border border-gray-300 dark:border-white/10 bg-bgSecondary dark:glass-effect">
              {[
                { key: "pdf", label: "Upload PDF", icon: FileText },
                { key: "paste", label: "Paste text", icon: ClipboardPaste },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                    tab === key
                      ? "bg-gradient-to-r from-pink-500 to-blue-500 text-white"
                      : "dark:text-gray-400 text-textSecondary hover:bg-black/5 dark:hover:bg-white/5"
                  }`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>

            {tab === "paste" ? (
              <div className="space-y-2">
                <textarea
                  rows={10}
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                  placeholder={SAMPLE}
                  className="gradient-ring dark:glass-effect bg-bgSecondary dark:!text-white !text-textPrimary border border-gray-300 dark:border-white/10 rounded-xl w-full px-4 py-3 text-sm outline-none font-mono resize-none"
                />
                <p className="text-xs dark:text-gray-500 text-textSecondary">
                  Separate questions with a blank line. Mark the correct option with a
                  trailing <span className="font-mono">*</span> or an{" "}
                  <span className="font-mono">Answer: B</span> line.
                </p>
                <button onClick={handleParseText} className="button w-full">
                  Preview
                </button>
              </div>
            ) : (
              <div className="space-y-2 py-4">
                <input
                  id="mcq-pdf-input"
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setFileName(file?.name || "");
                    handlePdf(file);
                  }}
                  className="hidden"
                />
                <label
                  htmlFor="mcq-pdf-input"
                  className="mx-auto flex w-56 flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 dark:border-white/15 bg-bgSecondary dark:glass-effect py-6 text-center cursor-pointer transition-colors hover:border-gray-400 dark:hover:border-white/30 hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <Upload size={18} className="dark:text-gray-400 text-textSecondary" />
                  <span className="text-sm dark:text-white text-textPrimary font-medium px-3 truncate max-w-full">
                    {fileName || "Click to upload PDF"}
                  </span>
                </label>
                {isParsing && (
                  <p className="text-xs text-center dark:text-gray-400 text-textSecondary">Reading PDF…</p>
                )}
              </div>
            )}
          </>
        )}

        {rows && (
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-emerald-400">
                <CheckCircle2 size={14} /> {valid.length} ready
              </span>
              {broken.length > 0 && (
                <span className="flex items-center gap-1.5 text-amber-400">
                  <AlertTriangle size={14} /> {broken.length} need attention
                </span>
              )}
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {rows.map((row, i) => (
                <div
                  key={i}
                  className={`px-4 py-3 rounded-xl border text-sm ${
                    row.error
                      ? "border-amber-400/40 bg-amber-500/5"
                      : "border-white/10 bg-bgSecondary dark:glass-effect"
                  }`}
                >
                  <p className="dark:text-white text-textPrimary font-medium">
                    {row.text || <span className="text-amber-400">(no question text)</span>}
                  </p>
                  <p className="text-xs dark:text-gray-400 text-textSecondary mt-1">
                    {row.options.length} options
                    {row.correctIndex >= 0 && !row.error && (
                      <> · correct: {String.fromCharCode(65 + row.correctIndex)}</>
                    )}
                  </p>
                  {row.error && <p className="text-xs text-amber-400 mt-1">{row.error}</p>}
                </div>
              ))}
            </div>

            {broken.length > 0 && (
              <p className="text-xs dark:text-gray-400 text-textSecondary">
                Rows needing attention are skipped. Go back and fix the source to include them.
              </p>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => setRows(null)}
                className="border-btn dark:border-btn-dark flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all"
              >
                Back
              </button>
              <button
                onClick={() => {
                  onAccept(valid.map(rowToQuestion));
                  onClose();
                }}
                disabled={!valid.length}
                className="button flex-1 disabled:opacity-50"
              >
                Add {valid.length} question{valid.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const summarise = (q) => (q.text || "").trim() || "Untitled question";

// The gradient-outline pill used for secondary actions across the app (View JD,
// the Back button in the import dialog). Padding and radius come from the class
// itself, so none is set here.
const addQuestionCls =
  "border-btn dark:border-btn-dark dark:!text-white !text-textPrimary flex items-center gap-1.5 transition-all";

/**
 * The question text, edited in the accordion header so it isn't rendered twice.
 * Grows to fit its content however long it gets — the whole question stays on
 * screen, with no inner scrollbar to hide part of it.
 */
const QuestionTextField = ({ value, onChange }) => {
  const ref = useRef(null);

  // Also runs on expand, so a question loaded from the server is sized correctly
  // the moment it opens rather than after the first keystroke.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // scrollHeight leaves out the border, but box-sizing is border-box, so it has
    // to be added back or the field sits 2px short of the line it contains.
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter your question here..."
      maxLength={500}
      className="w-full overflow-hidden resize-none rounded-lg border border-gray-300 dark:border-white/10 focus:border-purple-400 bg-bgPrimary dark:glass-effect dark:!text-white !text-textPrimary px-2.5 py-1.5 text-sm leading-5 outline-none transition-colors"
    />
  );
};

// `expandedId` and `showImport` are both owned by the builder, not here — the
// sidebar navigator opens questions too, and the sidebar's Import card opens this
// same dialog, so two owners of either state would fight.
const QuestionListPanel = ({
  questions = [],
  errors = {},
  onChange,
  expandedId,
  onExpandedChange,
  showImport,
  onShowImportChange,
}) => {
  const updateQuestion = (updated) =>
    onChange(questions.map((q) => (q.id === updated.id ? updated : q)));

  const addQuestion = () => {
    const q = newQuestion();
    onChange([...questions, q]);
    onExpandedChange(q.id);
  };

  const removeQuestion = (id) => onChange(questions.filter((q) => q.id !== id));

  return (
    <div className="flex-1 w-full min-w-0 space-y-3">
      {/* A count of zero is the one thing the empty state below already says, at length. */}
      {questions.length > 0 && (
        <h3 className="text-sm font-semibold dark:text-white text-textPrimary">
          Questions{" "}
          <span className="dark:text-gray-500 text-textSecondary font-normal">
            ({questions.length})
          </span>
        </h3>
      )}

      {questions.length === 0 && (
        // lg:w-[calc(100%+19.5rem)] extends the box past this column's own width by
        // exactly the sidebar's reserved width (w-72 + gap-6) — the sidebar renders
        // nothing at 0 questions, so this centers the message on the full page rather
        // than on the narrower column, while the box's left edge stays put at the
        // column's own left edge.
        <div className="min-h-[420px] flex items-center justify-center rounded-xl border border-dashed border-white/15 lg:w-[calc(100%+19.5rem)]">
          <div className="px-4 text-center space-y-6 ">
            <div>
              <p className="text-sm dark:text-gray-400 text-textSecondary">No questions yet.</p>
              <p className="text-xs dark:text-gray-500 text-textSecondary mt-1">
                Add one yourself, or import a batch by pasting text or uploading a PDF.
              </p>
            </div>
            <div className="flex items-center justify-center gap-6">
              <button onClick={addQuestion} className={addQuestionCls}>
                <Plus size={15} /> Add question
              </button>
              <button onClick={() => onShowImportChange(true)} className="button flex items-center gap-1.5">
                <Upload size={15} /> Import Questions
              </button>
            </div>
          </div>
        </div>
      )}

      {questions.map((q, i) => {
        const isOpen = expandedId === q.id;
        const error = errors[q.id];
        return (
          <div
            key={q.id}
            id={`mcq-q-${q.id}`}
            // `group` drives both hover affordances below: the tint on this row and
            // the delete button appearing. Only while collapsed — an open question is
            // already the focus of the page and doesn't need pointing at.
            className={`group rounded-xl border transition-colors scroll-mt-24 ${
              error
                ? "border-red-400/40"
                : "border-gray-300 dark:border-white/10 bg-bgSecondary dark:glass-effect"
            } ${isOpen ? "" : "hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"}`}
          >
            {/* Open, the bottom padding goes so the question sits tight against its own
                options; collapsed, that padding is the row height, so it stays. */}
            <div
              className={`flex gap-2 px-4 ${
                isOpen ? "pt-2.5 items-start" : "py-2.5 items-center"
              }`}
            >
              {/* Collapsed, the number and summary are one wide toggle target; open, the
                  number is only a label — the chevron at the right of the line is the
                  toggle, so typing in the field beside it can't collapse the question. */}
              {isOpen ? (
                <span className="text-sm font-semibold dark:text-gray-500 text-textSecondary shrink-0 w-5 text-right mt-1.5">
                  {i + 1}
                </span>
              ) : (
                <button
                  onClick={() => onExpandedChange(q.id)}
                  title="Expand question"
                  aria-expanded={false}
                  className="flex items-center gap-2 text-left flex-1 min-w-0"
                >
                  {/* Fixed width so 9 and 10 end at the same x and every summary starts
                      at the same x. */}
                  <span className="text-sm font-semibold dark:text-gray-500 text-textSecondary shrink-0 w-5 text-right">
                    {i + 1}
                  </span>
                  <span className="text-sm dark:text-white text-textPrimary truncate">
                    {summarise(q)}
                  </span>
                </button>
              )}

              {isOpen && (
                <div className="flex-1 min-w-0">
                  <QuestionTextField
                    value={q.text}
                    onChange={(text) => updateQuestion({ ...q, text })}
                  />
                  <InlineError msg={error} />
                </div>
              )}

              {error && (
                <AlertTriangle
                  size={14}
                  className={`text-red-400 shrink-0 ${isOpen ? "mt-2" : ""}`}
                  title={error}
                />
              )}
              <button
                onClick={() => removeQuestion(q.id)}
                title="Delete question"
                // Hidden rather than unmounted, so it stays tabbable — focus brings it
                // back for anyone not using a mouse.
                className={`p-1.5 rounded-md text-gray-400 dark:text-gray-600 hover:text-red-400 transition-all duration-150 shrink-0 ${
                  isOpen ? "mt-0.5" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
                }`}
              >
                <Trash2 size={14} />
              </button>

              {/* Last on the line, past a wider gap than the icons share — the one benign
                  control on this row shouldn't sit flush against delete. */}
              <button
                onClick={() => onExpandedChange(isOpen ? null : q.id)}
                title={isOpen ? "Collapse question" : "Expand question"}
                aria-expanded={isOpen}
                aria-label={`${isOpen ? "Collapse" : "Expand"} question ${i + 1}`}
                className={`p-1.5 ml-1 rounded-md dark:text-gray-400 text-textSecondary hover:text-textPrimary dark:hover:text-white transition-colors shrink-0 ${
                  isOpen ? "mt-0.5" : ""
                }`}
              >
                {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
            </div>

            {isOpen && (
              <div className="px-4 pb-3 pt-1.5 border-t border-white/10">
                <QuestionEditor question={q} onChange={updateQuestion} />
              </div>
            )}
          </div>
        );
      })}

      {questions.length > 0 && (
        <div className="flex justify-center pt-1">
          <button onClick={addQuestion} className={addQuestionCls}>
            <Plus size={15} /> Add question
          </button>
        </div>
      )}

      {showImport && (
        <ImportDialog
          onClose={() => onShowImportChange(false)}
          onAccept={(imported) => onChange([...questions, ...imported])}
        />
      )}
    </div>
  );
};

export default QuestionListPanel;
